import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ShopDirectory from './ShopDirectory';
import COMPANY from '../legal/company';
import './shop.css';

/** A store's address name from what someone types: "Annapoorna Foods" becomes annapoorna-foods. */
export const storeAddressName = (text) => String(text || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const MIN_LETTERS = 2;

/** The store-name box with suggestions: as the buyer types, stores whose name matches are offered (a few, from the server); choosing one opens it. */
function StoreFinder({ onOpen, compact = false }) {
  const [name, setName] = useState('');
  const [found, setFound] = useState(null); // null: nothing asked yet
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const typed = name.trim();

  useEffect(() => {
    if (typed.length < MIN_LETTERS) { setFound(null); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      fetch(`/api/public/shops/suggest?q=${encodeURIComponent(typed)}`)
        .then((r) => (r.ok ? r.json() : []))
        .then((list) => { if (live) { setFound(Array.isArray(list) ? list : []); setActive(-1); } })
        .catch(() => { if (live) setFound([]); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [typed]);

  const submit = (e) => {
    e.preventDefault();
    if (active >= 0 && found?.[active]) { onOpen(found[active].slug); return; }
    const slug = storeAddressName(name);
    if (slug) onOpen(slug);
  };
  const keys = (e) => {
    if (!found || found.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => (i + 1) % found.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i <= 0 ? found.length - 1 : i - 1)); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const showList = open && found && found.length > 0;
  return (
    <Form onSubmit={submit} autoComplete="off">
      <div className="position-relative">
        <InputGroup>
          <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
          <Form.Control placeholder={compact ? 'Open a store by its name' : "Type the store's name"} value={name} onChange={(e) => { setName(e.target.value); setOpen(true); }} onKeyDown={keys} onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)} aria-label="Store name" aria-autocomplete="list" aria-expanded={showList} role="combobox" data-testid="landing-store-name" />
          <Button type="submit" disabled={!storeAddressName(name)}>Open store</Button>
        </InputGroup>
        {showList && (
          <ul className="list-group position-absolute w-100 shadow mt-1 store-suggestions" style={{ zIndex: 1030 }} role="listbox" data-testid="store-suggestions">
            {found.map((s, i) => (
              <li key={s.slug} role="option" aria-selected={i === active} className={`list-group-item list-group-item-action d-flex align-items-center gap-3 ${i === active ? 'active' : ''}`} style={{ cursor: 'pointer' }}
                onMouseDown={(e) => { e.preventDefault(); onOpen(s.slug); }} data-testid="store-suggestion">
                {s.logoUrl ? <img src={s.logoUrl} alt="" style={{ width: 40, height: 40, objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /> : <span className="rounded bg-body-secondary d-inline-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}><i className="bi bi-shop" /></span>}
                <span className="flex-grow-1" style={{ minWidth: 0 }}>
                  <span className="d-block fw-semibold text-truncate">{s.name}</span>
                  <span className="d-block small opacity-75 text-truncate">{[s.tagline, s.place].filter(Boolean).join(' · ')}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {(!compact || (found && found.length === 0 && typed.length >= MIN_LETTERS)) && (
        <Form.Text className="text-muted" data-testid="store-finder-note">
          {found && found.length === 0 && typed.length >= MIN_LETTERS
            ? 'No store with that name is listed. Check the spelling, or ask the seller for their link.'
            : 'Start typing, for example "annapoorna". Not listed? Ask the seller for their link.'}
        </Form.Text>
      )}
    </Form>
  );
}

/**
 * ibigdo.com/shop: every business has its own store at /shop/<its name>. This page helps a buyer who has only a store's name to open it, tells a business how to
 * open its own, and (once some stores have chosen to) lets a buyer browse their products. There is no shared cart: a buyer goes on to the seller's own store to buy.
 */
export default function ShopLanding() {
  const navigate = useNavigate();
  // Whether any store shows its products: undefined until known, then the page makes room for them (a slim header, the products, a slim offer to sell) or keeps the fuller welcome
  const [hasProducts, setHasProducts] = useState(undefined);
  useEffect(() => {
    document.title = `Online stores - ${COMPANY.brand}`;
    return () => { document.title = COMPANY.brand; };
  }, []);

  return (
    <div className="shop d-flex flex-column min-vh-100" data-testid="shop-landing">
      <PublicHeader />
      <main className="shop-main">
        <Container className="py-4">
         {hasProducts !== false ? (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h1 className="h4 mb-1">Online stores on {COMPANY.brand}</h1>
              <p className="text-muted small mb-0">Products from businesses that make, process and trade. Each sale is made in the seller&apos;s own store.</p>
            </div>
            <div style={{ flex: '1 1 18rem', maxWidth: '26rem' }}><StoreFinder compact onOpen={(slug) => navigate(`/shop/${slug}`)} /></div>
          </div>
         ) : (
         <>
          <div className="shop-hero mb-4">
            <h1>Online stores on {COMPANY.brand}</h1>
            <p className="text-muted mb-0">Businesses that make, process and trade sell directly from stores of their own. Every store has its own address, at ibigdo.com/shop/<em>store-name</em>, and its own prices, delivery and payment.</p>
          </div>
          <Row className="g-4">
            <Col md={6}>
              <div className="shop-card p-4 h-100">
                <h2 className="section-title"><span className="step"><i className="bi bi-bag" /></span>Looking for a store?</h2>
                <p>Open the link the seller sent you. If you only have the store&apos;s name, type it here.</p>
                <StoreFinder onOpen={(slug) => navigate(`/shop/${slug}`)} />
              </div>
            </Col>
            <Col md={6}>
              <div className="shop-card p-4 h-100" data-testid="landing-sell">
                <h2 className="section-title"><span className="step"><i className="bi bi-shop" /></span>Sell online with {COMPANY.brand}</h2>
                <p>Open a store of your own from the stock you already keep.</p>
                <ul className="ps-3 mb-3">
                  <li>Buyers pay by card, UPI or net banking through your own Razorpay account, or by UPI, bank transfer or cash on delivery.</li>
                  <li>One click makes the GST tax invoice, with the stock and the receipt booked for you.</li>
                  <li>Shipping by weight and state, returns and refunds, packing slips and a sales report.</li>
                </ul>
                <div className="d-flex flex-wrap gap-2">
                  <Link to="/register" className="btn btn-primary">Start free trial</Link>
                  <a href="/guide/walkthroughs/sell-online/index.html" className="btn btn-outline-secondary" target="_blank" rel="noopener noreferrer">See how it works</a>
                  <Link to="/login" className="btn btn-link">Sign in</Link>
                </div>
              </div>
            </Col>
          </Row>
         </>
         )}
          <ShopDirectory onAvailable={setHasProducts} />
          {hasProducts && (
            <div className="shop-card p-3 mt-4 d-flex flex-wrap align-items-center justify-content-between gap-3" data-testid="landing-sell">
              <div style={{ flex: '1 1 20rem' }}>
                <strong>Sell online with {COMPANY.brand}</strong>
                <p className="small text-muted mb-0">Open a store of your own from the stock you already keep: GST tax invoices in one click, shipping, and payments into your own Razorpay account. Show your products here too, if you like.</p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Link to="/register" className="btn btn-primary btn-sm">Start free trial</Link>
                <a href="/guide/walkthroughs/sell-online/index.html" className="btn btn-outline-secondary btn-sm" target="_blank" rel="noopener noreferrer">See how it works</a>
                <Link to="/login" className="btn btn-link btn-sm">Sign in</Link>
              </div>
            </div>
          )}
        </Container>
      </main>
      <PublicFooter />
    </div>
  );
}
